import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import ForwardToInboxRoundedIcon from "@mui/icons-material/ForwardToInboxRounded";
import { Button, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import { combineComparers, distinct, priorityComparer } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { Contact, ContactWithFieldValues } from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import { getInvestorPortalImpersonationUrl } from "../../../../utilities/navigationHelper";
import { useFilterContext } from "../../../common/filters/FilterContext";
import type { FilterState } from "../../../common/filters/filterState";
import { getAppliedMultiSelectFilterLabels } from "../../../common/filters/filterState";
import { getCheckedGridSx } from "../../../common/grid/gridStyles";
import LockedGridCell from "../../../common/LockedGridCell";
import { LockedInvestorPortalPopover } from "../common/LockedPortalPagePopovers";
import { ContactPortalStatus } from "./contactsGridDefinitions";
import ContactWarning from "./ContactWarning";

type ContactTab = "main" | "documents" | "emails";

const getContactPath = (contactId: string, tab: ContactTab): string => `./${contactId}/${tab}`;

interface GetContactsGridColumnsOptions {
  excludedColumns: string[];
  filterState: FilterState<ContactWithFieldValues>;
  isInvestorPortalAppEnabled: boolean;
  canInspectInvestorPortal: boolean;
  hasAccessToManage: boolean;
  onNavigateToContact: (data: ContactWithFieldValues, tab: ContactTab) => void;
  onDeleteContact: (contact: ContactWithFieldValues) => void;
  onImpersonate: (email: string) => void;
}

const getContactsGridColumns = ({
  excludedColumns,
  filterState,
  isInvestorPortalAppEnabled,
  canInspectInvestorPortal,
  hasAccessToManage,
  onNavigateToContact,
  onDeleteContact,
  onImpersonate,
}: GetContactsGridColumnsOptions): GridColDef<ContactWithFieldValues>[] => {
  const filterInvestorLabels = getAppliedMultiSelectFilterLabels("investor", filterState);
  const filterFundLabels = getAppliedMultiSelectFilterLabels("fund", filterState);

  const investorNamesSortComparer = combineComparers(
    priorityComparer<string>((value) => filterInvestorLabels.includes(value)),
    (a, b) => a.localeCompare(b)
  );

  const fundNamesSortComparer = combineComparers(
    priorityComparer<string>((value) => filterFundLabels.includes(value)),
    (a, b) => a.localeCompare(b)
  );

  const columns: GridColDef<ContactWithFieldValues>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      renderCell: ({ row }) => (
        <Stack spacing={0.5}>
          <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
            <GridTextHoverLink to={getContactPath(row.id, "main")}>
              <Typography variant="subtitle2" noWrap>
                {row.name}
              </Typography>
            </GridTextHoverLink>
            {row.warningsCodes.includes("LastEmailDeliveryFailed") && (
              <ContactWarning
                title="Email Delivery Failure"
                description="The last email sent to the contact has failed to be delivered successfully. Click the 'View' button to review the email delivery log for the contact."
                onNavigate={() => {
                  onNavigateToContact(row, "emails");
                }}
              />
            )}
          </Stack>
          {row.email && (
            <Typography color="textSecondary" variant="caption">
              {row.email}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: "fundInvestors.investorName",
      headerName: "Investor(s)",
      flex: 1.5,
      renderCell: ({ row }) =>
        row.fundInvestors.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList
            displayCount={1}
            items={distinct(row.fundInvestors.map((fi) => fi.investorName))}
            propGetter={(name) => name}
            sortComparer={investorNamesSortComparer}
          />
        ),
    },
    {
      field: "fundInvestors.fundName",
      headerName: "Fund(s)",
      flex: 1.5,
      renderCell: ({ row }) =>
        row.fundInvestors.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList
            displayCount={1}
            items={distinct(row.fundInvestors.map((fi) => fi.fundName))}
            propGetter={(name) => name}
            sortComparer={fundNamesSortComparer}
          />
        ),
    },
  ];

  if (isInvestorPortalAppEnabled) {
    columns.push(
      {
        field: "hasAccessToInvestorPortal",
        headerName: "Access to Portal",
        resizable: false,
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (row.hasAccessToInvestorPortal ? <CheckRoundedIcon color="primary" /> : <></>),
      },
      {
        field: "status",
        headerName: "Registration",
        resizable: false,
        flex: 1,
        minWidth: 130,
        renderCell: ({ row }) => <ContactPortalStatus status={row.status} registrationDate={row.registrationDate} />,
      },
      {
        field: "invitationSentAt",
        headerName: "Invited to Portal",
        resizable: false,
        width: 160,
        renderCell: ({ row }) => (row.invitationSentAt ? convertISODateTime(row.invitationSentAt) : "-"),
      },
      {
        field: "lastActivity",
        headerName: "Last Activity",
        resizable: false,
        width: 160,
        renderCell: ({ row }) =>
          row.lastActivity
            ? convertISODateTime(row.lastActivity)
            : row.hasUndatedActivity
              ? "Yes — date not recorded"
              : "-",
      },
      {
        field: "totalLiveMessages",
        headerName: "Live Documents",
        type: "number",
        resizable: false,
        width: 120,
        renderCell: ({ row }) => (row.totalLiveMessages < 1 ? "-" : row.totalLiveMessages),
      }
    );
  } else {
    columns.push({
      field: "portalActivity",
      resizable: false,
      width: 160,
      renderHeader: () => (
        <Stack spacing={0.5} direction="row" sx={{ width: "100%", alignItems: "center" }}>
          <Typography color="textDisabled">Portal Activity</Typography>
          <LockedInvestorPortalPopover />
        </Stack>
      ),
      renderCell: () => <LockedGridCell />,
    });
  }

  if (canInspectInvestorPortal || hasAccessToManage) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 72,
      cellClassName: "grid-row-actions",
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => {
        return (
          <ActionsMenuButton
            items={[
              {
                icon: <DesktopWindowsRoundedIcon sx={{ color: "action.active" }} />,
                label: "Impersonate",
                hidden: !canInspectInvestorPortal,
                disabled: !row.hasAccessToInvestorPortal || !row.email,
                onClick: () => onImpersonate(row.email),
              },
              {
                icon: <DeleteOutlineIcon color="error" />,
                label: "Delete",
                hidden: !hasAccessToManage,
                onClick: () => onDeleteContact(row),
              },
            ]}
          />
        );
      },
    });
  }

  return columns.filter((col) => !excludedColumns.includes(col.field));
};

interface Props {
  allRows: ContactWithFieldValues[];
  excludedColumns?: string[];
  isLoading: boolean;
  onInviteContacts: (contactIds: string[]) => void;
  onDeleteContacts: (contactIds: string[]) => void;
  onDeleteContact: (contact: Contact) => void;
}

const ContactsGrid = ({
  allRows,
  excludedColumns,
  isLoading,
  onInviteContacts,
  onDeleteContacts,
  onDeleteContact,
}: Props) => {
  const { investorPortalApp } = useClientContext();
  const navigate = useNavigate();

  const { clientCode, hasAnyPermission } = useClientContext();
  const { filterState } = useFilterContext<ContactWithFieldValues>();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const showToolbar = useMemo(() => Boolean(selectedIds.length), [selectedIds.length]);

  const hasAccessToSendInvitation = hasAnyPermission(["ManageFundStructure"]);
  const hasAccessToManage = hasAnyPermission(["ManageFundStructure"]);

  const canInspectInvestorPortal = useMemo(
    () => Boolean(investorPortalApp) && hasAnyPermission(["ViewInvestorPortalOnBehalfOfInvestor"]),
    [hasAnyPermission, investorPortalApp]
  );

  const handleNavigateToContact = useCallback(
    (contact: Contact, tab: ContactTab) => {
      navigate(getContactPath(contact.id, tab));
    },
    [navigate]
  );

  const handleImpersonate = useCallback(
    (userEmail: string) => {
      const investorPortalUrl = getInvestorPortalImpersonationUrl(clientCode, { target: "Investor", userEmail });
      window.open(investorPortalUrl, "_blank");
    },
    [clientCode]
  );

  const columns = useMemo(
    () =>
      getContactsGridColumns({
        excludedColumns: excludedColumns ?? [],
        filterState,
        isInvestorPortalAppEnabled: Boolean(investorPortalApp),
        canInspectInvestorPortal,
        hasAccessToManage,
        onNavigateToContact: handleNavigateToContact,
        onDeleteContact,
        onImpersonate: handleImpersonate,
      }),
    [
      canInspectInvestorPortal,
      excludedColumns,
      filterState,
      handleNavigateToContact,
      handleImpersonate,
      hasAccessToManage,
      investorPortalApp,
      onDeleteContact,
    ]
  );

  const isSendInvitationDisabledForSelectedRows = allRows.some(
    (contact) => selectedIds.includes(contact.id) && !contact.hasAccessToInvestorPortal
  );

  const isCheckboxSelectionEnabled = (investorPortalApp && hasAccessToSendInvitation) || hasAccessToManage;

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedIds, setSelectedIds]}
      actions={
        <>
          {investorPortalApp && hasAccessToSendInvitation && (
            <Button
              disabled={isLoading || isSendInvitationDisabledForSelectedRows}
              onClick={() => onInviteContacts(selectedIds)}
              color="secondary"
              variant="text"
              startIcon={<ForwardToInboxRoundedIcon />}
            >
              Send Invitation
            </Button>
          )}
          {hasAccessToManage && (
            <Button
              disabled={isLoading}
              onClick={() => onDeleteContacts(selectedIds)}
              color="error"
              variant="text"
              startIcon={<DeleteOutlineIcon />}
            >
              Delete
            </Button>
          )}
        </>
      }
    >
      <DataGrid<ContactWithFieldValues>
        checkboxSelection={isCheckboxSelectionEnabled}
        rowSelectionModel={getRowSelectionModelFromIds(selectedIds)}
        onRowSelectionModelChange={(newSelection) => setSelectedIds(getSelectedRowIdsFromModel(newSelection))}
        sx={getCheckedGridSx}
        columns={columns}
        rows={allRows}
        loading={isLoading}
        multilineRows
        noRowsText="No contacts"
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        shiftActionsLeft
        disableColumnSorting
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default ContactsGrid;
